import { makeAutoObservable } from "mobx";
import { ICompareValueStore } from "./compare-value-factory";
import KeyValueCompareValueField from "../../components/policy-editor/policy-eligibility-card/condition-group-card/condition-builder/condition-field/compare-value-type-fields/key-value-compare-value-field";
import { CONDITION_OPERATORS } from "../../policy-types";

export interface IKeyValueCompareValue {
    value: string;
    key: string;
}

class KeyValueCompareValueStore implements ICompareValueStore<IKeyValueCompareValue> {
    private _model: IKeyValueCompareValue = {} as IKeyValueCompareValue;
    private _changes: Partial<IKeyValueCompareValue> = {};
    private _uncommittedChanges: Partial<IKeyValueCompareValue> = {};

    constructor(compareValue?: string) {
        makeAutoObservable(this);
        if (compareValue) {
            this._model = this._deserializeModel(compareValue);
        }
    }

    public setChanges(changes?: string) {
        if (changes) {
            this._changes = this._deserializeModel(changes);
        }
    }

    private _deserializeModel(compareValue: string): IKeyValueCompareValue {
        let model = {} as IKeyValueCompareValue;
        try {
            model = JSON.parse(compareValue);
        }
        catch(e) {
            /** should never happen */
            console.error(`invalid condition's compare value from DB: ${compareValue}`);
        }
        return model;
    }

    get Component() {
        return KeyValueCompareValueField;
    }

    public get value() {
        return this._uncommittedChanges.value ?? this._changes.value ?? this._model.value;
    }

    public get key() {
        return this._uncommittedChanges.key ?? this._changes.key ?? this._model.key;
    }

    public setValue(v: string) {
        this._uncommittedChanges.value = v;
    }

    public setKey(v: string) {
        this._uncommittedChanges.key = v;
    }

    public commitChanges(): void {
        this._changes = {...this._changes, ...this._uncommittedChanges};
        this._clearUnneededChanges();
    }

    public discardUncommittedChanges(): void {
        this._uncommittedChanges = {};
    }

    public hasChanges(): boolean {
        return Object.values(this._changes).length > 0;
    }

    public discardChanges(): void {
        this._changes = {};
    }

    public onOperatorChanged(op: CONDITION_OPERATORS): void {
        if ([CONDITION_OPERATORS.OP_IS_EMPTY, CONDITION_OPERATORS.OP_HAS_ANY_VALUE].includes(op)) {
            this._uncommittedChanges.value = '';
        }        
    }

    /** should not return empty serialized object, otherwise will interfer with isGhost method that expects undefined when info */
    public getUpdatedModel(): string|undefined {
        const updatedModel = JSON.stringify({ ...this._model, ...this._changes });
        if (updatedModel === JSON.stringify({})) {
            return undefined;
        }
        return updatedModel;
    }

    public getUncommittedChanges() {
        return { ...this._model, ...this._changes, ...this._uncommittedChanges };
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IKeyValueCompareValue;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }
}

export default KeyValueCompareValueStore;
